import { useState, useEffect, useRef, useCallback } from 'react';
import {
  bucketeerConfig,
  createBucketeerUser,
  BUCKETEER_FEATURE_IDS,
  initializeBKTClient,
  getBKTClient,
  destroyBKTClient,
} from '@/utils/bucketeer';

const FLAGS = [
  {
    key: BUCKETEER_FEATURE_IDS.ONBOARDING_DEMO_ACCOUNT,
    label: 'Onboarding Demo Account',
    type: 'string',
    defaultValue: 'onboarding',
    variants: {
      onboarding: { bg: '#e0f2fe', border: '#0284c7', color: '#0369a1', label: 'Full onboarding (v1/v2 via onboarding-version)' },
      'demo-account': { bg: '#fef9c3', border: '#ca8a04', color: '#a16207', label: 'Auto demo login on /create-account' },
    },
  },
  {
    key: BUCKETEER_FEATURE_IDS.JOYRIDE_ONBOARDING,
    label: 'Joyride Onboarding',
    type: 'boolean',
    defaultValue: false,
    variants: {
      true: { bg: '#dcfce7', border: '#16a34a', color: '#15803d', label: 'Show Joyride Tour' },
      false: { bg: '#fee2e2', border: '#dc2626', color: '#b91c1c', label: 'Hide Joyride Tour' },
    },
  },
  {
    key: BUCKETEER_FEATURE_IDS.ONBOARDING_VERSION,
    label: 'Onboarding Version',
    type: 'string',
    defaultValue: 'onboarding-v2',
    variants: {
      'onboarding-v1': { bg: '#e0f2fe', border: '#0284c7', color: '#0369a1', label: 'Old /create-account (V1)' },
      'onboarding-v2': { bg: '#dcfce7', border: '#16a34a', color: '#15803d', label: 'New /create-account (V2)' },
    },
  },
  {
    key: BUCKETEER_FEATURE_IDS.FRONTEND_AB_VERSION,
    label: 'Frontend A/B Version',
    type: 'string',
    defaultValue: 'a',
    variants: {
      a: { bg: '#e0f2fe', border: '#0284c7', color: '#0369a1', label: 'Variant A (Main)' },
      b: { bg: '#fef9c3', border: '#ca8a04', color: '#a16207', label: 'Variant B (Snapshot)' },
    },
  },
];

const evaluateFlag = (client, flag) => {
  if (flag.type === 'boolean') {
    return String(client.booleanVariation(flag.key, flag.defaultValue));
  }
  return client.stringVariation(flag.key, flag.defaultValue);
};

const BucketeerTestPage = () => {
  const [selectedFlagIdx, setSelectedFlagIdx] = useState(0);
  const [userId, setUserId] = useState(() => crypto.randomUUID());
  const [variant, setVariant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const isMounted = useRef(true);
  const batchAbort = useRef(false);

  const flag = FLAGS[selectedFlagIdx];

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    setVariant(null);
    setHistory([]);
    setBatchResults(null);
    const id = crypto.randomUUID();
    setUserId(id);
    fetchVariant(id, FLAGS[selectedFlagIdx]);
  }, [selectedFlagIdx]);

  const fetchVariant = async (id, flagConfig) => {
    if (!bucketeerConfig) {
      setError('Bucketeer not configured. Check VITE_APP_BUCKETEER_API_KEY.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      destroyBKTClient();
      const user = createBucketeerUser(id);
      await initializeBKTClient(bucketeerConfig, user);
      const result = evaluateFlag(getBKTClient(), flagConfig);
      if (!isMounted.current) return;
      setVariant(result);
      setHistory((prev) => [{ userId: id, variant: result, ts: new Date().toLocaleTimeString() }, ...prev.slice(0, 49)]);
    } catch (err) {
      if (!isMounted.current) return;
      setError(err.message);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleGenerate = () => {
    const newId = crypto.randomUUID();
    setUserId(newId);
    fetchVariant(newId, flag);
  };

  const handleBatchTest = useCallback(async () => {
    if (!bucketeerConfig) return;
    setBatchRunning(true);
    batchAbort.current = false;
    const counts = {};
    const total = 50;

    for (let i = 0; i < total; i++) {
      if (batchAbort.current) break;
      try {
        destroyBKTClient();
        const id = crypto.randomUUID();
        const user = createBucketeerUser(id);
        await initializeBKTClient(bucketeerConfig, user);
        const result = evaluateFlag(getBKTClient(), flag);
        counts[result] = (counts[result] || 0) + 1;
        if (isMounted.current) {
          setBatchResults({ ...counts, _total: i + 1 });
        }
      } catch {
        // skip failed evaluations
      }
    }
    if (isMounted.current) setBatchRunning(false);
  }, [flag]);

  const style = variant ? flag.variants[variant] : null;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui, sans-serif', padding: '40px 24px' }}>
      <div style={{ maxWidth: 740, margin: '0 auto' }}>

        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
          Bucketeer Flag Tester
        </h1>
        <p style={{ color: '#64748b', marginBottom: 24, fontSize: 14 }}>
          Pick a flag, generate random user IDs, or run a batch to verify distribution.
        </p>

        {/* Flag selector */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
          {FLAGS.map((f, i) => (
            <button
              key={f.key}
              onClick={() => setSelectedFlagIdx(i)}
              style={{
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: selectedFlagIdx === i ? 700 : 500,
                background: selectedFlagIdx === i ? '#6366f1' : '#fff',
                color: selectedFlagIdx === i ? '#fff' : '#475569',
                border: `1.5px solid ${selectedFlagIdx === i ? '#6366f1' : '#e2e8f0'}`,
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
          padding: '10px 16px', marginBottom: 20, fontSize: 13, color: '#64748b',
        }}>
          Flag: <code style={{ color: '#0f172a', fontWeight: 600 }}>{flag.key}</code>
          &nbsp;&middot;&nbsp;Type: <code>{flag.type}</code>
          &nbsp;&middot;&nbsp;Default: <code>{String(flag.defaultValue)}</code>
        </div>

        {/* Variant display */}
        <div style={{
          background: style ? style.bg : '#f1f5f9',
          border: `2px solid ${style ? style.border : '#cbd5e1'}`,
          borderRadius: 12,
          padding: '32px 28px',
          marginBottom: 24,
          textAlign: 'center',
          transition: 'all 0.2s',
        }}>
          {loading ? (
            <p style={{ fontSize: 28, color: '#94a3b8', fontWeight: 600 }}>Fetching...</p>
          ) : error ? (
            <p style={{ fontSize: 16, color: '#dc2626', fontWeight: 500 }}>{error}</p>
          ) : (
            <>
              <p style={{ fontSize: 48, fontWeight: 800, color: style?.color ?? '#0f172a', margin: 0, letterSpacing: -1 }}>
                {variant ?? '-'}
              </p>
              <p style={{ fontSize: 16, color: style?.color ?? '#64748b', marginTop: 8, fontWeight: 500 }}>
                {style?.label ?? ''}
              </p>
            </>
          )}
        </div>

        {/* Current user ID */}
        <div style={{ background: '#f1f5f9', borderRadius: 8, padding: '12px 16px', marginBottom: 20, wordBreak: 'break-all' }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Current User ID</span>
          <p style={{ fontSize: 13, color: '#334155', fontFamily: 'monospace', margin: '4px 0 0' }}>{userId}</p>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 36 }}>
          <button
            onClick={handleGenerate}
            disabled={loading || batchRunning}
            style={{
              flex: 1,
              padding: '14px 24px',
              fontSize: 15,
              fontWeight: 600,
              background: loading || batchRunning ? '#94a3b8' : '#6366f1',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: loading || batchRunning ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {loading ? 'Loading...' : 'Generate random user'}
          </button>

          <button
            onClick={batchRunning ? () => { batchAbort.current = true; } : handleBatchTest}
            disabled={loading}
            style={{
              flex: 1,
              padding: '14px 24px',
              fontSize: 15,
              fontWeight: 600,
              background: batchRunning ? '#dc2626' : '#0f172a',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {batchRunning ? `Stop (${batchResults?._total ?? 0}/50)` : 'Batch test (50 users)'}
          </button>
        </div>

        {/* Batch results */}
        {batchResults && (
          <div style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
              Batch Results ({batchResults._total} evaluated)
            </h2>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {Object.entries(batchResults)
                .filter(([k]) => k !== '_total')
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([value, count]) => {
                  const s = flag.variants[value];
                  const pct = ((count / batchResults._total) * 100).toFixed(1);
                  return (
                    <div
                      key={value}
                      style={{
                        flex: 1,
                        minWidth: 160,
                        background: s?.bg ?? '#f1f5f9',
                        border: `2px solid ${s?.border ?? '#cbd5e1'}`,
                        borderRadius: 10,
                        padding: '20px 16px',
                        textAlign: 'center',
                      }}
                    >
                      <p style={{ fontSize: 32, fontWeight: 800, color: s?.color ?? '#0f172a', margin: 0 }}>
                        {count}
                      </p>
                      <p style={{ fontSize: 14, color: s?.color ?? '#64748b', marginTop: 4, fontWeight: 600 }}>
                        {value} ({pct}%)
                      </p>
                      <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                        {s?.label ?? ''}
                      </p>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
              History (last {history.length})
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {history.map((entry, i) => {
                const s = flag.variants[entry.variant];
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderLeft: `4px solid ${s?.border ?? '#cbd5e1'}`,
                      borderRadius: 8,
                      padding: '10px 14px',
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 700, color: s?.color ?? '#0f172a', minWidth: 120 }}>
                      {entry.variant}
                    </span>
                    <span style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.userId}
                    </span>
                    <span style={{ fontSize: 11, color: '#cbd5e1' }}>{entry.ts}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BucketeerTestPage;
